import React, { useState } from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import { CloudUpload, Visibility, Close } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { uploadFile } from '@utils/utils';
import { showError, showSuccess } from '@components/notification';

const ImageUploadCCCD = ({ imageUrl, label, onUploadSuccess, defaultImage, showUploadHint = false }) => {
  const classes = useStyles();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(imageUrl);
  const [openLightbox, setOpenLightbox] = useState(false);

  const handleFileChange = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError({ message: 'Vui lòng chọn file ảnh hợp lệ!' });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError({ message: 'Kích thước file không được vượt quá 5MB!' });
        return;
      }

      setUploading(true);
      setUploadProgress(0);

      const res = await uploadFile(file, (progress) => setUploadProgress(progress));

      if (res?.data?.data) {
        setPreviewUrl(res.data.data);
        showSuccess({ message: 'Upload ảnh thành công!' });
        if (onUploadSuccess) {
          onUploadSuccess(res.data.data);
        }
      }
    } catch (error) {
      showError({ message: 'Không thể upload ảnh. Vui lòng thử lại!' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleOpenLightbox = () => {
    if (previewUrl && previewUrl !== defaultImage) {
      setOpenLightbox(true);
    }
  };

  const handleCloseLightbox = () => {
    setOpenLightbox(false);
  };

  React.useEffect(() => {
    setPreviewUrl(imageUrl);
  }, [imageUrl]);

  return (
    <div className={classes.container}>
      <div className={`${classes.imageContainer} ${showUploadHint ? classes.uploadHintContainer : ''}`}>
        {showUploadHint && (
          <div className={classes.uploadHintOverlay}>
            <CloudUpload className={classes.uploadHintIcon} />
            <Typography variant="caption" className={classes.uploadHintText}>
              Chưa có ảnh
            </Typography>
            <Typography variant="caption" className={classes.uploadHintSubtext}>
              Click để upload
            </Typography>
          </div>
        )}
        <img
          src={previewUrl || defaultImage}
          alt={label}
          className={classes.image}
          onClick={handleOpenLightbox}
          style={{ cursor: previewUrl && previewUrl !== defaultImage ? 'pointer' : 'default' }}
          onError={(e) => {
            e.target.src = defaultImage;
          }}
        />
        {previewUrl && previewUrl !== defaultImage && (
          <div className={classes.imageOverlay} onClick={handleOpenLightbox}>
            <Visibility className={classes.viewIcon} />
          </div>
        )}
      </div>

      <div className={classes.labelContainer}>
        <span className={classes.label}>{label}</span>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id={`upload-${label}`}
          type="file"
          onChange={handleFileChange}
          disabled={uploading}
        />
        <label htmlFor={`upload-${label}`}>
          <IconButton
            color="primary"
            aria-label="upload picture"
            component="span"
            size="small"
            disabled={uploading}
            sx={{
              bgcolor: showUploadHint ? '#2D9764' : 'transparent',
              color: showUploadHint ? '#fff' : 'inherit',
              '&:hover': {
                bgcolor: showUploadHint ? '#237d52' : 'rgba(45, 151, 100, 0.04)'
              }
            }}>
            <CloudUpload fontSize="small" />
          </IconButton>
        </label>
      </div>

      {uploading && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Image Lightbox Dialog */}
      <Dialog open={openLightbox} onClose={handleCloseLightbox} maxWidth="lg">
        <DialogActions sx={{ p: 1 }}>
          <IconButton onClick={handleCloseLightbox} size="small">
            <Close />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ p: 2 }}>
          <img
            src={previewUrl}
            alt={label}
            style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative'
  },
  imageContainer: {
    position: 'relative',
    textAlign: 'center',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '8px',
    backgroundColor: '#f9f9f9',
    overflow: 'hidden',
    '&:hover $imageOverlay': {
      opacity: 1
    }
  },
  uploadHintContainer: {
    border: '2px dashed #2D9764',
    backgroundColor: '#f8fff9',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f0fff1',
      borderColor: '#237d52'
    }
  },
  uploadHintOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
    textAlign: 'center',
    color: '#2D9764',
    pointerEvents: 'none'
  },
  uploadHintIcon: {
    fontSize: '48px',
    marginBottom: '8px',
    opacity: 0.7
  },
  uploadHintText: {
    display: 'block',
    fontWeight: 600,
    fontSize: '12px'
  },
  uploadHintSubtext: {
    display: 'block',
    fontSize: '10px',
    opacity: 0.8
  },
  image: {
    width: '100%',
    height: '200px',
    objectFit: 'cover',
    borderRadius: '8px',
    transition: 'all 0.3s ease'
  },
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    cursor: 'pointer',
    borderRadius: '8px',
    margin: '8px'
  },
  viewIcon: {
    color: '#fff',
    fontSize: '48px'
  },
  labelContainer: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  label: {
    fontSize: '14px',
    fontWeight: 500
  }
}));

export default ImageUploadCCCD;
